// src/offline/offlineDownloadStore.ts
import { create } from "zustand";
import {
  DownloadController,
  type DownloadProgress,
  type Prefix,
} from "./offlineDownload";
import {
  getDownloadedPrefixes,
  addDownloadedPrefix,
  setDownloadStats,
  refreshStorageEstimate,
} from "./offlineMeta";
import { downloadQueue } from "./downloadQueue";
import { db } from "@/db";
import { useToastStore } from "@/ui/toastStore";

export type DownloadStatus =
  | "idle"
  | "queued"
  | "starting"
  | "downloading"
  | "cancelling"
  | "can_resume"
  | "done"
  | "error"
  | "cancelled"
  | "deleting"
  | "delete_done"
  | "delete_error";

export interface PrefixJob {
  status: DownloadStatus;
  progress?: DownloadProgress | null;
  controller?: DownloadController | null;
  error?: string | null;
  isDownloaded: boolean;
}

interface OfflineDownloadStore {
  jobs: Record<string, PrefixJob>;
  pushToast: (msg: string, severity?: "success" | "error" | "info" | "warning") => void;
  offlinePausedNotified: boolean;

  setJob: (prefix: string, job: Partial<PrefixJob>) => void;
  ensureJob: (prefix: string) => Promise<void>;
  resumeQueuedDownloads: () => Promise<void>;
  pauseAllDownloads: (reason?: "offline") => void;
  cancelAllDownloads: () => void;
  clearJob: (prefix: string) => void;
}

export const useOfflineDownloadStore = create<OfflineDownloadStore>(
  (set, get) => ({
    jobs: {},
    pushToast: useToastStore.getState().push, // injected
    offlinePausedNotified: false,

    setJob(prefix, job) {
      const prev = get().jobs[prefix];
      const base: PrefixJob = {
        status: "idle",
        progress: null,
        controller: null,
        error: null,
        isDownloaded: false,
      };

      set({
        jobs: {
          ...get().jobs,
          [prefix]: {
            ...base,
            ...prev,
            ...job,
          },
        },
      });
    },

    async ensureJob(prefix) {
      if (!get().jobs[prefix]) {
        const downloaded = (await getDownloadedPrefixes()).includes(prefix);
        const progressMeta = await db.meta.get(`offlineDownloadProgress_${prefix}`);
        const hasProgress = !!progressMeta?.value;
        set({
          jobs: {
            ...get().jobs,
            [prefix]: {
              status: hasProgress ? "can_resume" : "idle",
              progress: null,
              controller: null,
              error: null,
              isDownloaded: downloaded,
            },
          },
        });
      }
    },

    async resumeQueuedDownloads() {
      const prefixes = Object.keys(get().jobs);
      const pushToast = get().pushToast;
      // reset offline notification so we can notify again after an offline pause
      if (get().offlinePausedNotified) {
        set({ offlinePausedNotified: false });
      }
      for (const prefix of prefixes) {
        const job = get().jobs[prefix];
        if (!job) continue;
        if (job.status !== "queued" && job.status !== "can_resume") continue;

        if (!["LS", "YO", "WF", "HG"].includes(prefix)) {
          continue;
        }

        const controller = new DownloadController();
        let lastDownloadedCount = job.progress?.addressesDownloaded ?? 0;
        get().setJob(prefix, {
          controller,
          status: "queued",
          error: null,
        });

        try {
          const result = await downloadQueue.add(prefix as Prefix, {
            controller,
            onProgress: (progress) => {
              lastDownloadedCount = progress.addressesDownloaded ?? lastDownloadedCount;
              get().setJob(prefix, { progress });
            },
          });

          if (!result.ok) {
            if (result.cancelled) {
              get().setJob(prefix, { status: "cancelled", progress: null });
            } else if (result.paused || result.error === "offline") {
              get().setJob(prefix, { status: "can_resume", error: null });
            } else {
              get().setJob(prefix, { status: "can_resume", error: result.error });
              pushToast(`Download ${prefix} failed: ${result.error ?? "Unknown error"}`, "error");
            }
            continue;
          }

          await addDownloadedPrefix(prefix);
          await setDownloadStats(prefix, {
            addressesDownloaded: lastDownloadedCount,
            updatedAt: new Date().toISOString(),
          });
          await refreshStorageEstimate();
          get().setJob(prefix, {
            status: "done",
            progress: null,
            isDownloaded: true,
          });
          pushToast(`Download ${prefix} completed`, "success");
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : "Download failed";
          get().setJob(prefix, {
            status: "can_resume",
            error: msg,
          });
          pushToast(`Download ${prefix} failed: ${msg}`, "error");
        }
      }
    },

    pauseAllDownloads(reason = "offline") {
      const jobs = { ...get().jobs };
      Object.entries(jobs).forEach(([prefix, job]) => {
        if (["downloading", "queued", "starting"].includes(job.status)) {
          job.controller?.pause?.();
          jobs[prefix] = {
            ...job,
            status: "can_resume",
            controller: job.controller,
          };
        }
      });
      downloadQueue.clearWaiting();
      const alreadyNotified = get().offlinePausedNotified;
      if (reason === "offline" && !alreadyNotified) {
        get().pushToast("Downloads paused while offline", "info");
        set({ offlinePausedNotified: true, jobs });
      } else {
        set({ jobs });
      }
    },

    cancelAllDownloads() {
      const jobs = { ...get().jobs };
      Object.entries(jobs).forEach(([prefix, job]) => {
        if (["downloading", "queued", "starting", "cancelling"].includes(job.status)) {
          job.controller?.cancel?.();
          jobs[prefix] = {
            ...job,
            status: "cancelled",
            progress: null,
            controller: null,
          };
        }
      });
      downloadQueue.cancelAll();
      set({ jobs });
    },

    clearJob(prefix) {
      const jobs = { ...get().jobs };
      delete jobs[prefix];
      set({ jobs });
    },
  })
);



// // src/offline/offlineDownloadStore.ts
// import { create } from "zustand";
// import { DownloadController, type DownloadProgress } from "./offlineDownload";
// import { getDownloadedPrefixes } from "./offlineMeta";

// export type DownloadStatus =
//   | "idle"
//   | "queued"
//   | "starting"
//   | "downloading"
//   | "can_resume"
//   | "done"
//   | "error"
//   | "cancelled"
//   | "deleting"
//   | "delete_done"
//   | "delete_error";

// export interface PrefixJob {
//   status: DownloadStatus;
//   progress?: DownloadProgress | null;
//   controller?: DownloadController | null;
//   error?: string | null;
//   isDownloaded: boolean;
// }

// interface OfflineDownloadStore {
//   jobs: Record<string, PrefixJob>;
//   setJob: (prefix: string, job: Partial<PrefixJob>) => void;
//   ensureJob: (prefix: string) => Promise<void>;
//   clearJob: (prefix: string) => void;
// }

// export const useOfflineDownloadStore = create<OfflineDownloadStore>((set, get) => ({
//   jobs: {},

//   setJob(prefix, job) {
//     const prev = get().jobs[prefix] || { status: "idle", isDownloaded: false };
//     set({
//       jobs: {
//         ...get().jobs,
//         [prefix]: { ...prev, ...job },
//       },
//     });
//   },

//   async ensureJob(prefix) {
//     const existing = get().jobs[prefix];
//     if (!existing) {
//       const downloaded = (await getDownloadedPrefixes()).includes(prefix);
//       set({
//         jobs: {
//           ...get().jobs,
//           [prefix]: { status: "idle", isDownloaded: downloaded },
//         },
//       });
//     }
//   },

//   clearJob(prefix) {
//     const updated = { ...get().jobs };
//     delete updated[prefix];
//     set({ jobs: updated });
//   },
// }));
